import { type ReactNode } from 'react';
import { type NeedsAttentionItem } from 'xosign-types';
import IcCross from '@/assets/icons/Cross.svg?react';
import IcTimer from '@/assets/icons/Timer.svg?react';
import { daysSince, daysUntil } from './dayDistance.util';

export type Tone = 'warning' | 'neutral' | 'error';

export interface RowCopy {
  tone: Tone;
  icon: ReactNode;
  title: string;
  subtitle: string;
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

const buildRow = (item: NeedsAttentionItem, t: Translate): RowCopy => {
  switch (item.type) {
    case 'deadline_approaching':
      return {
        tone: 'warning',
        icon: <IcTimer />,
        title: item.signing.name,
        subtitle: t('common:dashboard.needsAttention.deadlineApproaching', {
          count: daysUntil(item.expirationDate),
        }),
      };
    case 'review_pending_too_long': {
      const reviewerName = item.reviewer?.name?.trim() ?? '';
      return {
        tone: 'warning',
        icon: <IcTimer />,
        title: item.signing.name,
        subtitle: reviewerName
          ? t('common:dashboard.needsAttention.reviewPendingTooLong', {
              name: reviewerName,
            })
          : t('common:dashboard.needsAttention.reviewPendingTooLongAnonymous'),
      };
    }
    case 'stale_unsigned':
      return {
        tone: 'neutral',
        icon: <IcTimer />,
        title: item.signing.name,
        subtitle: t('common:dashboard.needsAttention.staleUnsigned', {
          count: daysSince(item.sentAt),
        }),
      };
    case 'expired':
      return {
        tone: 'error',
        icon: <IcCross />,
        title: item.signing.name,
        subtitle: t('common:dashboard.needsAttention.expired'),
      };
    case 'review_rejected': {
      const reviewerName = item.reviewer?.name?.trim() ?? '';
      return {
        tone: 'error',
        icon: <IcCross />,
        title: reviewerName
          ? t('common:dashboard.needsAttention.reviewRejected', {
              name: reviewerName,
              signing: item.signing.name,
            })
          : t('common:dashboard.needsAttention.reviewRejectedAnonymous', {
              signing: item.signing.name,
            }),
        subtitle: t('common:dashboard.needsAttention.reviewRejectedSubtitle'),
      };
    }
  }
};

export default buildRow;
